import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import CurrencyRate from 'components/CurrencyRate';
import Row from 'components/Layout/Row';
import TransactionAllocator, {
  Transaction,
} from 'components/TransactionAllocator';
import {
  ActionRef,
  ActionTransactionSubTypes,
  ActionType,
  ActionTypes,
} from 'further-types/lp/action';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { Column } from 'further-ui/components/Layout';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { revaluation as rules } from 'further-ui/validations';
import useCurrencyRate from 'hooks/lp/data/currencies/useCurrencyRate';
import useActionTransactions from 'hooks/lp/ui/transactions/useActionTransactions';
import api from 'lib/trpcClient';
import { compact, sumBy } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { ProcessedRevaluation } from '../../helpers/processRevaluationHistory';

interface HoldingClassTransactionAllocatorProps {
  revaluation: ProcessedRevaluation;
  transactionTypeOptions: Array<{ label: string; value: string | null }>;
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
  assetId: string;
}

const HoldingClassTransactionAllocator: React.FC<
  HoldingClassTransactionAllocatorProps
> = ({ revaluation, transactionTypeOptions, vehicle, assetId }) => {
  const { setValue, watch, getValues } =
    useFormContext<rules.RevaluationFormType>();

  const { data: unrealisedGains, isLoading: isUnrealisedGainsLoading } =
    api.lpRevaluation.getUnrealisedGains.useQuery(
      {
        assetId,
        vehicleId: vehicle?._id ?? '',
        holdingClassId: revaluation.holdingClassId,
        revaluationDate: new CalendarDay(revaluation.date),
        newUnitPriceTcy: revaluation.newUnitPrice,
      },
      {
        enabled: !!assetId && !!vehicle?._id,
      },
    );

  const { fxRate, changeRate, isFetchingRate } = useCurrencyRate({
    vehicleId: vehicle._id,
    currency: revaluation.currency,
    date: new CalendarDay(revaluation.date),
    defaultCurrencyRate:
      getValues(`revaluationCurrencyRates.${revaluation.holdingClassId}`) ??
      null,
    onRateChange: (rate) => {
      setValue(
        `revaluationCurrencyRates.${revaluation.holdingClassId}`,
        rate ?? undefined,
      );
    },
  });

  const defaultRevaluationUpTransactions = useActionTransactions({
    vehicleId: vehicle._id,
    action: {
      actionRef: ActionRef.Revaluation,
      actionType: ActionTypes.CompanyRevaluationUp,
      amountIncVatTcy: 0,
      amountExVatTcy: 0,
      currency: revaluation.currency,
    },
    transactions: undefined,
  });

  const defaultRevaluationDownTransactions = useActionTransactions({
    vehicleId: vehicle._id,
    action: {
      actionRef: ActionRef.Revaluation,
      actionType: ActionTypes.CompanyRevaluationDown,
      amountIncVatTcy: 0,
      amountExVatTcy: 0,
      currency: revaluation.currency,
    },
    transactions: undefined,
  });

  const existingTransactions =
    watch(`revaluationTransactions.${revaluation.holdingClassId}`) ?? [];

  const transactions =
    compact(
      unrealisedGains?.flatMap((investmentUnrealisedGain, index) => {
        const actionType =
          investmentUnrealisedGain.stepUpTcy > 0
            ? ActionTypes.CompanyRevaluationUp
            : ActionTypes.CompanyRevaluationDown;

        const defaultTransactions =
          actionType === ActionTypes.CompanyRevaluationUp
            ? defaultRevaluationUpTransactions
            : defaultRevaluationDownTransactions;

        return defaultTransactions.map(
          (defaultTransaction, transactionIndex) => {
            // Calculate a consistent index for this transaction across renders
            const globalTransactionIndex =
              index * defaultTransactions.length + transactionIndex;

            const existingTransaction =
              existingTransactions[globalTransactionIndex];

            if (
              existingTransaction &&
              existingTransaction.actionType === actionType
            ) {
              return {
                ...existingTransaction,
                amountIncVatTcy: Math.abs(investmentUnrealisedGain.stepUpTcy),
                amountExVatTcy: Math.abs(investmentUnrealisedGain.stepUpTcy),
                investmentId: investmentUnrealisedGain.investmentId,
                holdingClassId: revaluation.holdingClassId,
                actionType,
              };
            } else {
              return {
                ...defaultTransaction,
                amountIncVatTcy: Math.abs(investmentUnrealisedGain.stepUpTcy),
                amountExVatTcy: Math.abs(investmentUnrealisedGain.stepUpTcy),
                label: `${investmentUnrealisedGain.investmentDate}`,
                investmentId: investmentUnrealisedGain.investmentId,
                actionType,
              };
            }
          },
        );
      }),
    ) ?? [];

  const onTransactionChange = (
    transactions: Array<
      Transaction & { investmentId: string; actionType: ActionType }
    >,
    holdingClassId: string,
  ) => {
    setValue(
      `revaluationTransactions.${holdingClassId}`,
      transactions.map((t) => ({
        ...t,
        id: t.id?.toString(),
        amountIncVatLcy: t.amountIncVatLcy ?? 0,
        vatRate: 0,
        transactionSubType: ActionTransactionSubTypes.CreditDebitPair,
      })),
    );
  };

  if (isUnrealisedGainsLoading) {
    return null;
  }

  return (
    <Accordion defaultExpanded disableGutters>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Row centered justify="space-between" fullWidth>
          <Typography variant="h5">{revaluation.holdingClassName}</Typography>
          <Typography variant="h5" color="text.secondary">
            {numberToCurrencyString(sumBy(unrealisedGains, 'stepUpTcy') ?? 0, {
              currency: revaluation.currency,
            })}
          </Typography>
        </Row>
      </AccordionSummary>
      <AccordionDetails>
        <Column spacing="xs">
          <CurrencyRate
            currency={revaluation.currency}
            date={new CalendarDay(revaluation.date)}
            vehicle={vehicle}
            fxRate={fxRate}
            onRateChange={changeRate}
            isFetchingRate={isFetchingRate}
          />
          <TransactionAllocator
            transactionTypeOptions={transactionTypeOptions ?? []}
            transactions={transactions}
            onChange={(transactions) =>
              onTransactionChange(transactions, revaluation.holdingClassId)
            }
            transactionCurrency={revaluation.currency}
            fxRate={fxRate}
            vehicle={vehicle}
          />
        </Column>
      </AccordionDetails>
    </Accordion>
  );
};

export default HoldingClassTransactionAllocator;
